/**
 * Input types that support policy constraints for validation
 *
 * The actual constraint logic for the BE will be implemented post MVP.
 * The contract currently supports the following types.
 */
export enum ConstraintSupportedInputType {
  Uint = 'uint',
  Int = 'int',
  Address = 'address',
  Bool = 'bool',
  String = 'string',
  Bytes = 'bytes',
}

/**
 * Common base fields for all parameters
 */
type BaseContractFunctionParameter = {
  name: string;
};

/**
 * Numeric parameter (uint / int) – has bit size
 */
type NumericContractFunctionParameter = BaseContractFunctionParameter & {
  constraintsSupported: true;
  type: ConstraintSupportedInputType.Uint | ConstraintSupportedInputType.Int;
  /**
   * Bit size for uint/int, e.g. 8, 16, 32, 64, 128, 256.
   * For bare "uint"/"int", you can normalize to 256.
   */
  bitSize: number;
};

type BytesContractFunctionParameter = BaseContractFunctionParameter & {
  constraintsSupported: true;
  type: ConstraintSupportedInputType.Bytes;
  /**
   * Byte length for fixed-size bytes (e.g., 32 for bytes32), or 'dynamic' for dynamic bytes
   */
  bytesLength: number | 'dynamic';
};

/**
 * Non-numeric constraint-supported parameter (address, bool, string)
 */
type NonNumericContractFunctionParameter = BaseContractFunctionParameter & {
  constraintsSupported: true;
  type:
    | ConstraintSupportedInputType.Address
    | ConstraintSupportedInputType.Bool
    | ConstraintSupportedInputType.String;
};

// Enforce tuple list size to be a positive integer in the type level/
// We need this TupleContractFunctionParameter type to be able to represent the tuple list size in the type level.
// Lists are more dynamic and simple but tuples have fields with their types inside them so we need to represent this in the type level.
type PositiveInt = Exclude<`${bigint}`, `-${string}` | '0'>;
export type TupleContractFunctionParameter = BaseContractFunctionParameter & {
  constraintsSupported: false;
  type: 'tuple' | 'tuple[]' | `tuple[${PositiveInt}]`;
  components: ContractFunctionParameter[];
};

/**
 * Parameter types that don't support constraints (arrays, unknown types, etc.)
 * Just stores the Solidity type string for display purposes
 * Note: tuples have their own dedicated type (TupleContractFunctionParameter)
 */
type OtherUnsupportedContractFunctionParameter =
  BaseContractFunctionParameter & {
    constraintsSupported: false;
    /**
     * The unsupported Solidity type, e.g., "address[]", "uint256[5]", "fixed", etc.
     */
    type: string;
  };

export type ContractFunctionParameter =
  | NumericContractFunctionParameter
  | NonNumericContractFunctionParameter
  | BytesContractFunctionParameter
  | TupleContractFunctionParameter
  | OtherUnsupportedContractFunctionParameter;
/**
 * Represents a contract function from an ABI
 */
export type ContractFunction = {
  name: string;
  selector: string;
  inputs: ContractFunctionParameter[];
  stateMutability: 'nonpayable' | 'payable';
};

/**
 * Response from contract ABI details API endpoint
 */
export enum ContractAbiDetailsType {
  ERC20 = 'ERC-20',
  CONTRACT = 'CONTRACT',
  ERROR = 'ERROR',
}

export enum ContractAbiDetailsErrorCode {
  INVALID_ADDRESS = 'INVALID_ADDRESS',
  NOT_A_CONTRACT = 'NOT_A_CONTRACT',
  UNVERIFIED_CONTRACT = 'UNVERIFIED_CONTRACT',
  EXPLORER_ERROR = 'EXPLORER_ERROR',
  RPC_UNAVAILABLE = 'RPC_UNAVAILABLE',
  UNKNOWN = 'UNKNOWN',
}

export type ContractAbiDetails =
  | {
      type: ContractAbiDetailsType.ERC20;
      contractName: string;
      proxyFunctions: ContractFunction[];
      implementationFunctions: ContractFunction[];
      tokenMetadata: {
        symbol: string;
        name: string;
        decimals: number;
        logoUrl: string | null;
      };
    }
  | {
      type: ContractAbiDetailsType.CONTRACT;
      contractName: string;
      proxyFunctions: ContractFunction[];
      implementationFunctions: ContractFunction[];
    }
  | {
      type: ContractAbiDetailsType.ERROR;
      contractName: null;
      proxyFunctions: ContractFunction[];
      implementationFunctions: ContractFunction[];
      errorCode: ContractAbiDetailsErrorCode;
      /**
       * Human-readable message suitable for UI display.
       *
       * The FE can use this to decide how to render the contract selector/function selector.
       */
      message: string;
    };
